import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { X, Send, Bot, Loader2 } from 'lucide-react';
import { AIMessage } from '../types';

interface AIChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  householdId: string;
  conversationId: string | null;
}

export default function AIChatModal({ isOpen, onClose, userId, householdId, conversationId: initialConversationId }: AIChatModalProps) {
  const [conversationId, setConversationId] = useState<string | null>(initialConversationId);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setConversationId(initialConversationId);
  }, [initialConversationId]);

  useEffect(() => {
    if (!isOpen || !conversationId) {
      if (!conversationId) setMessages([]);
      return;
    }

    const q = query(
      collection(db, 'ai_messages'),
      where('conversationId', '==', conversationId),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AIMessage));
      setMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });

    return () => unsubscribe();
  }, [isOpen, conversationId]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const textToSend = input;
    setInput('');
    setLoading(true);

    try {
      let currentConvId = conversationId;
      
      // Create new conversation if none exists
      if (!currentConvId) {
        const newConvRef = doc(collection(db, 'ai_conversations'));
        currentConvId = newConvRef.id;
        
        await setDoc(newConvRef, {
          householdId,
          userId,
          title: textToSend.substring(0, 30) + (textToSend.length > 30 ? '...' : ''),
          lastMessageTime: serverTimestamp(),
          createdAt: serverTimestamp()
        });
        
        setConversationId(currentConvId);
      }
      
      const sendMessage = httpsCallable(functions, 'sendMessageToAI');
      await sendMessage({ conversationId: currentConvId, text: textToSend });
      
    } catch (err) {
      console.error("Failed to send message:", err);
      alert("Failed to send message to AI.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg h-[80vh] flex flex-col shadow-2xl overflow-hidden relative">
        <header className="bg-blue-600 p-4 flex justify-between items-center text-white shrink-0">
          <div className="flex items-center gap-2">
            <div className="bg-white/20 p-2 rounded-lg">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-sm">AI Strategist</h2>
              <p className="text-[10px] text-blue-100 opacity-80">Brainstorm & align priorities</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
          {messages.length === 0 && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-50 space-y-4">
              <Bot className="w-12 h-12 text-slate-400" />
              <div>
                <p className="text-sm font-semibold text-slate-600">Start a new conversation</p>
                <p className="text-xs text-slate-400 max-w-[200px] mx-auto mt-1">Ask for advice, set strict goals, or brainstorm.</p>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div key={msg.id || idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                  msg.role === 'user' 
                    ? 'bg-blue-600 text-white rounded-tr-sm' 
                    : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-sm'
                }`}>
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  Strategist is thinking...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <form onSubmit={handleSend} className="p-4 bg-white border-t border-slate-200 shrink-0">
          <div className="relative flex items-center">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Message the AI Strategist..."
              className="w-full pl-4 pr-12 py-3 bg-slate-100 border-transparent rounded-full focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm outline-none"
              disabled={loading}
            />
            <button 
              type="submit" 
              disabled={!input.trim() || loading}
              className="absolute right-2 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
